// @ts-nocheck
import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';

export interface NotificationAnalytics {
  id: string;
  notification_id?: string;
  template_id?: string;
  event_type: 'sent' | 'opened' | 'clicked' | 'bounced' | 'unsubscribed';
  recipient_email: string;
  user_agent?: string;
  ip_address?: string;
  link_url?: string;
  tracking_pixel_id?: string;
  metadata: Record<string, any>;
  created_at: string;
}

export interface AnalyticsStats {
  total_sent: number;
  total_opened: number;
  total_clicked: number;
  total_bounced: number;
  total_unsubscribed: number;
  open_rate: number;
  click_rate: number;
  bounce_rate: number;
  unsubscribe_rate: number;
}

export interface TemplateAnalytics extends AnalyticsStats {
  template_id: string;
  template_name: string;
}

export const useNotificationAnalytics = () => {
  const [analytics, setAnalytics] = useState<NotificationAnalytics[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchAnalytics = async (templateId?: string, dateRange?: { start: Date; end: Date }) => {
    try {
      setIsLoading(true);
      let query = supabase
        .from('notification_analytics')
        .select('*')
        .order('created_at', { ascending: false });

      if (templateId) {
        query = query.eq('template_id', templateId);
      }

      if (dateRange) {
        query = query
          .gte('created_at', dateRange.start.toISOString())
          .lte('created_at', dateRange.end.toISOString());
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching analytics:', error);
        return;
      }

      setAnalytics(data || []);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const trackEvent = async (eventData: Omit<NotificationAnalytics, 'id' | 'created_at'>) => {
    try {
      const { error } = await supabase
        .from('notification_analytics')
        .insert([eventData]);

      if (error) {
        console.error('Error tracking event:', error);
        return false;
      }

      // Refresh analytics data
      await fetchAnalytics();
      return true;
    } catch (error) {
      console.error('Error tracking event:', error);
      return false;
    }
  };

  const getAnalyticsStats = (templateId?: string): AnalyticsStats => {
    const relevantAnalytics = templateId 
      ? analytics.filter(a => a.template_id === templateId)
      : analytics;

    const total_sent = relevantAnalytics.filter(a => a.event_type === 'sent').length;
    const total_opened = relevantAnalytics.filter(a => a.event_type === 'opened').length;
    const total_clicked = relevantAnalytics.filter(a => a.event_type === 'clicked').length;
    const total_bounced = relevantAnalytics.filter(a => a.event_type === 'bounced').length;
    const total_unsubscribed = relevantAnalytics.filter(a => a.event_type === 'unsubscribed').length;

    return {
      total_sent,
      total_opened,
      total_clicked,
      total_bounced,
      total_unsubscribed,
      open_rate: total_sent > 0 ? (total_opened / total_sent) * 100 : 0,
      click_rate: total_sent > 0 ? (total_clicked / total_sent) * 100 : 0,
      bounce_rate: total_sent > 0 ? (total_bounced / total_sent) * 100 : 0,
      unsubscribe_rate: total_sent > 0 ? (total_unsubscribed / total_sent) * 100 : 0,
    };
  };

  const getTemplateAnalytics = async (): Promise<TemplateAnalytics[]> => {
    try {
      // Get template analytics with template names
      const { data, error } = await supabase
        .from('notification_analytics')
        .select(`
          template_id,
          event_type,
          notification_templates!inner(name)
        `)
        .not('template_id', 'is', null);

      if (error) {
        console.error('Error fetching template analytics:', error);
        return [];
      }

      // Group by template_id and calculate stats
      const templateStats: Record<string, any> = {};

      data.forEach((record: any) => {
        const templateId = record.template_id;
        const templateName = record.notification_templates.name;

        if (!templateStats[templateId]) {
          templateStats[templateId] = {
            template_id: templateId,
            template_name: templateName,
            sent: 0,
            opened: 0,
            clicked: 0,
            bounced: 0,
            unsubscribed: 0,
          };
        }

        templateStats[templateId][record.event_type]++;
      });

      // Convert to TemplateAnalytics format
      return Object.values(templateStats).map((stats: any) => ({
        template_id: stats.template_id,
        template_name: stats.template_name,
        total_sent: stats.sent,
        total_opened: stats.opened,
        total_clicked: stats.clicked,
        total_bounced: stats.bounced,
        total_unsubscribed: stats.unsubscribed,
        open_rate: stats.sent > 0 ? (stats.opened / stats.sent) * 100 : 0,
        click_rate: stats.sent > 0 ? (stats.clicked / stats.sent) * 100 : 0,
        bounce_rate: stats.sent > 0 ? (stats.bounced / stats.sent) * 100 : 0,
        unsubscribe_rate: stats.sent > 0 ? (stats.unsubscribed / stats.sent) * 100 : 0,
      }));
    } catch (error) {
      console.error('Error getting template analytics:', error);
      return [];
    }
  };

  const generateTrackingPixel = () => {
    return crypto.randomUUID();
  };

  return {
    analytics,
    isLoading,
    fetchAnalytics,
    trackEvent,
    getAnalyticsStats,
    getTemplateAnalytics,
    generateTrackingPixel,
    refetch: fetchAnalytics
  };
};